import React from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { LayoutIcon } from '../../Icons/DropDownIcons/Layout';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import {
  CONTENT_SIZE_DEFAULT,
  FORM_LINE_TITLE_SIZE,
  MAX_SIZE_VALUE,
  MIN_SIZE_VALUE,
  MOBILE_CAROUSEL_CONTENT_SIZE_DEFAULT,
  SECTION_TITLE_SIZE,
} from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { createChangeSettings } from '../utils/createChangeSettings';
import { LayoutSelector } from '../selectors/LayoutSelector';
import { SettingsCheckbox } from '../subcomponents/SettingsCheckbox';
import { FormLine } from '@src/components/Form/FormLine';
import { Input } from '@src/components/Input';
import { WidgetView } from 'common';
import { MobileContentSizeSelector } from '../selectors/MobileContentSizeSelector';
import { Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { CarouselTypeSelector } from '../selectors/CarouselTypeSelector';
import { resolveCarouselTypeSelection } from '../utils/carouselType';

const Layout = ({ opened, onOpen, editedWidget, setEditedWidget }: SectionProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  const mustDisplayContentSize = (): boolean => {
    const { type } = editedWidget.settings;
    return type === 'wall' || type === 'carousel';
  };

  const isCarouselLayout = (): boolean => {
    return editedWidget.settings.type === 'carousel';
  };

  const changeType = (type: WidgetView['settings']['type'] = 'wall') => {
    if (type === editedWidget.settings.type) {
      return;
    }
    if (['carousel', 'wall'].includes(type)) {
      changeSettings({
        content_size: CONTENT_SIZE_DEFAULT,
      });
    }
    if (type === 'carousel' && !editedWidget.settings.mobile_carousel_content_size) {
      changeSettings({
        mobile_carousel_content_size: MOBILE_CAROUSEL_CONTENT_SIZE_DEFAULT,
      });
    }
    changeSettings({ type });
  };

  const selectedCarouselType = resolveCarouselTypeSelection(
    editedWidget.settings.carousel_type,
    editedWidget.settings.shop_this_look_display,
  );

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <LayoutIcon />
          {lang.title}
        </SectionTitle>
      }
      opened={opened}
      afterTitle={
        <Tooltip title={lang.tooltips.layout} placement="top">
          <span className={infoLayoutClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
      onOpen={onOpen}
    >
      <SectionContent>
        <LayoutSelector 
          onChange={(type) => changeType(type)} 
          editedWidget={editedWidget}
        />

        {isCarouselLayout() ? (
          <FormLine title={lang.fields.carouselType} size={FORM_LINE_TITLE_SIZE}>
            <CarouselTypeSelector
              value={selectedCarouselType}
              onChange={(type) => changeSettings({ carousel_type: type })}
            />
          </FormLine>
        ) : null}

        {!mustDisplayContentSize() || (
          <>
            <FormLine title={lang.fields.contentsPerRow} size={FORM_LINE_TITLE_SIZE}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                <Input
                  type='number'
                  onChange={({ target }) => {
                    let value = Math.min(Number(target.value) || 0, MAX_SIZE_VALUE);
                    value = Math.max(value, MIN_SIZE_VALUE);
                    changeSettings({
                      content_size: value,
                    });
                  }}
                  value={editedWidget.settings.content_size}
                />
              </div>
            </FormLine>
          </>
        )}

        {isCarouselLayout() ? (
          <FormLine title={lang.fields.mobileContentsPerRow} size={FORM_LINE_TITLE_SIZE}>
            <MobileContentSizeSelector 
              value={editedWidget.settings.mobile_carousel_content_size || MOBILE_CAROUSEL_CONTENT_SIZE_DEFAULT}
              onChange={(value) => changeSettings({ mobile_carousel_content_size: value })}
            />
          </FormLine>
        ) : null}

        <SettingsCheckbox
          label={lang.fields.randomOrder}
          checked={editedWidget.settings.random}
          onClick={() =>
            changeSettings({
              random: !editedWidget.settings.random,
            })
          }
        />

        <SettingsCheckbox
          label={lang.fields.prioritizeInfluencerContent}
          checked={Boolean(editedWidget.settings.prioritize_influencer_content)}
          onClick={() =>
            changeSettings({
              prioritize_influencer_content: !editedWidget.settings.prioritize_influencer_content,
            })
          }
        />
      </SectionContent>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    title: 'Layout',
    tooltips: {
      layout: 'Choose how your UGCs are displayed and configure the layout settings',
    },
    fields: { 
      randomOrder: 'UGCs in random order', 
      contentsPerRow: 'Content per row',
      mobileContentsPerRow: 'Content per row in mobile mode',
      prioritizeInfluencerContent: 'Prioritize influencer content',
      carouselType: 'Carousel Type',
    },
  },
});

const infoLayoutClass = style({
  marginLeft: 10,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

export default Layout;
